import { ElMessage, ElNotification } from "element-plus";
import type { AxiosError } from "axios";
import type { ApiResponse } from "@/shared/types";

/**
 * API 에러를 사용자 친화적인 메시지로 처리 (토스트 표시)
 */
export function handleApiError(error: unknown, customMessage?: string): void {
  console.error("API Error:", error);
  ElMessage.error(getApiErrorMessage(error, customMessage));
}

/**
 * API 에러에서 사용자에게 보여줄 메시지만 뽑는다.
 *
 * 토스트가 아니라 화면 안에 에러 상태를 그려야 하는 경우(DataState)에 쓴다.
 */
export function getApiErrorMessage(
  error: unknown,
  customMessage?: string,
): string {
  let message = customMessage || "오류가 발생했습니다";

  if (isAxiosError(error)) {
    const responseData = error.response?.data as
      | (ApiResponse & { errMsg?: string })
      | undefined;

    // 서버에서 제공한 메시지 우선 (errMsg: 신규 에러 포맷, message: 기존 포맷)
    if (responseData?.errMsg) {
      message = responseData.errMsg;
    } else if (responseData?.message) {
      message = responseData.message;
    } else {
      // HTTP 상태 코드별 메시지
      switch (error.response?.status) {
        case 400:
          message = "잘못된 요청입니다";
          break;
        case 401:
          message = "인증이 필요합니다";
          break;
        case 403:
          message = "권한이 없습니다";
          break;
        case 404:
          message = "요청한 리소스를 찾을 수 없습니다";
          break;
        case 409:
          message = "이미 존재하는 데이터입니다";
          break;
        case 500:
          message = "서버 오류가 발생했습니다";
          break;
        case 503:
          message = "서비스를 사용할 수 없습니다";
          break;
        default:
          if (error.message) {
            message = error.message;
          }
      }
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return message;
}

/**
 * 성공 메시지 표시
 */
export function showSuccess(message: string = "성공했습니다"): void {
  ElMessage.success(message);
}

/**
 * 알림 메시지 표시
 */
export function showNotification(
  title: string,
  message: string,
  type: "success" | "warning" | "info" | "error" = "info",
): void {
  ElNotification({
    title,
    message,
    type,
  });
}

/**
 * Axios 에러인지 확인하는 타입 가드
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}
